import React, { useState, useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const INACTIVITY_LIMIT = 5 * 60 * 1000; // 5 minutes

const PrivateRoute = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const location = useLocation();
  const inactivityTimer = useRef(null);

  const logout = () => {
    localStorage.removeItem("token");
    setIsLoggedIn(false);
    setShowPopup(true);
  };

  const checkInitialTokenValidity = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      logout();
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        logout();
      } else {
        setIsLoggedIn(true);
      }
    } catch (error) {
      console.error("Invalid token:", error);
      logout();
    }
  };

  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      logout(); // logout after inactivity
    }, INACTIVITY_LIMIT);
  };

  useEffect(() => {
    checkInitialTokenValidity();

    const events = ["mousemove", "keydown", "scroll", "click"];
    events.forEach(event => window.addEventListener(event, resetInactivityTimer));

    resetInactivityTimer();

    return () => {
      clearTimeout(inactivityTimer.current);
      events.forEach(event => window.removeEventListener(event, resetInactivityTimer));
    };
  }, []);

  if (isLoggedIn === null) {
    return <div>Loading...</div>;
  }

  if (!isLoggedIn && showPopup) {
    return (
      <div style={styles.overlay}>
        <div style={styles.popup}>
          <h2 style={styles.popupTitle}>Session Expired</h2>
          <p style={styles.popupMessage}>You have been logged out due to inactivity.</p>
          <button
            style={styles.button}
            onClick={() => setShowPopup(false)}
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  return isLoggedIn ? children : <Navigate to="/" state={{ from: location }} />;
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  popup: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "10px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
    textAlign: "center",
    width: "300px",
  },
  popupTitle: {
    fontSize: "20px",
    fontWeight: "bold",
    marginBottom: "10px",
    color: "#333",
  },
  popupMessage: {
    fontSize: "16px",
    color: "#666",
    marginBottom: "20px",
  },
  button: {
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "16px",
  },
};

export default PrivateRoute;
